/**
 * One Business Unit Group (f4.1) -- the Details tab, and only that tab.
 *
 * The `Artifacts` tab the brief specifies for this screen is **absent, not
 * disabled**: artifacts arrive in phase 15, and f3.1 set the precedent of
 * omitting a control whose backing concept does not exist rather than
 * shipping one that explains itself away.
 *
 * The four provenance values shown here are not columns. They are read from
 * f1.4's audit log and carry its `actor_display` **snapshot**, so renaming a
 * user never rewrites what this screen says about a change they made three
 * cycles ago -- the property a foreign key to `app_user` would destroy, and
 * the reason this screen and f1.5's viewer can never disagree.
 *
 * f4.2 added the `Ownership` card beside the Details tab -- the prototype's
 * own card from `client-detail.html`. It is where a DEM and a CEM are named,
 * which is what the plan asks for: *"a person can be named DEM and CEM on a
 * Business Unit Group **from the detail screen**"*. An owner is an Atlas user
 * account and every account exists from its first sign-in -- or from the
 * moment an Admin pre-provisions it -- so naming one here is always possible.
 * What the create dialog does not DEMAND is an owner at birth: a group is
 * routinely recorded before anybody has decided who will run it.
 */

import { ArrowLeft, FileText, Info } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useBusinessUnitGroup, useLookupCatalog } from '@/api/queries';
import { ArtifactsPanel } from '@/routes/artifacts/ArtifactsPanel';
import { ASSIGN_OWNER, PUBLISH_ARTIFACT, rowCan } from '@/session/permissions';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import { TabHost } from '@/ui/TabHost';

// f5.1 moved this component to `@/ownership` and parameterised it by node
// type, so the Business Units screen renders the SAME card rather than a copy.
// Nothing about what it does here changed.
import { OwnershipCard } from '@/ownership/OwnershipCard';
import { DELIVERY_ROLE_TYPE, GROUP_ROLE_CODES } from './ownershipView';

import {
  PROVENANCE_MISSING,
  badgeClassFor,
  formatActor,
  formatMoment,
  initialsOf,
} from './groupView';

function BackLink() {
  return (
    <Link className="btn btn-secondary btn-sm" to="/business-unit-groups">
      <ArrowLeft aria-hidden="true" /> Business Unit Groups
    </Link>
  );
}

/** One label-and-value pair in the Details tab. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="detail-meta-item">
      <div className="label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/**
 * A timestamp and the actor stored beside it on the row.
 *
 * The name is the account's CURRENT one. f1.5's audit viewer shows the name as
 * it was when the change happened, so the two can differ after a rename --
 * deliberately: this says who owns the row, that says who performed the act.
 */
function Provenance({
  label,
  at,
  by,
}: {
  label: string;
  at: string | null | undefined;
  by: { display: string } | null | undefined;
}) {
  const actor = formatActor(by);
  return (
    <Fact label={label}>
      <div>{formatMoment(at)}</div>
      <span className="row group-actor">
        {actor === PROVENANCE_MISSING ? (
          <span className="hint">{actor}</span>
        ) : (
          <>
            <span className="avatar sm" aria-hidden="true">
              {initialsOf(actor)}
            </span>
            {actor}
          </>
        )}
      </span>
    </Fact>
  );
}

export function BusinessUnitGroupDetailPage() {
  const { groupId = '' } = useParams();
  // f9.2: the route guard in `App.tsx` reads the navigation key. The request
  // is always ISSUED for the id, and the state below is chosen from the
  // response -- the client never pre-checks an id against anything.
  const detail = useBusinessUnitGroup(groupId, { enabled: Boolean(groupId) });
  // The SAME hook every other screen uses. The delivery roles are values of
  // a registered lookup type, so they arrive with the catalogue and need no
  // endpoint of their own.
  const catalog = useLookupCatalog();
  const deliveryRoles =
    catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [];

  if (detail.isPending) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">Business Unit Group</h1>
          </div>
          <div className="page-actions">
            <BackLink />
          </div>
        </div>
        <div className="card" aria-busy="true" aria-label="Loading the group">
          <div className="card-body">
            {[0, 1, 2, 3].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (detail.error || !detail.data) {
    // One shared state for all four levels. A 404 does not say whether the
    // group is missing or merely not the caller's (Q-f9.1-3).
    return (
      <RecordUnavailable
        error={detail.error}
        backTo="/business-unit-groups"
        backLabel="Business Unit Groups"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const group = detail.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{group.name}</h1>
          <div className="page-sub">
            <span className="mono">{group.code}</span>, top level of the GFT US hierarchy
          </div>
        </div>
        <div className="page-actions">
          <BackLink />
        </div>
      </div>

      {/* f15.1: the shared TabHost -- Details unchanged, plus Artifacts. */}
      <TabHost
        label="Business Unit Group"
        tabs={[
          {
            id: 'details',
            label: 'Details',
            icon: <Info aria-hidden="true" />,
            panel: (
            <div className="detail-columns">
              <div className="card">
                <div className="card-body">
                  <div className="detail-meta">
                    <Fact label="Code">
                      <span className="mono">{group.code}</span>
                    </Fact>
                    <Fact label="Name">{group.name}</Fact>
                    {/* f5.1's count, on the tab that already exists. No new tab:
                        a count is a fact about this group, not a second view of it. */}
                    <Fact label="Business Units">
                      {group.business_unit_count === 0 ? (
                        <span className="hint">None active yet</span>
                      ) : (
                        <Link
                          className="cell-link"
                          to={`/business-units?group=${encodeURIComponent(group.id)}`}
                        >
                          {group.business_unit_count} active
                        </Link>
                      )}
                    </Fact>
                    <Fact label="Status">
                      <span className={badgeClassFor(group.status.code)}>
                        {group.status.name}
                      </span>
                    </Fact>
                    <Provenance label="Created" at={group.created_at} by={group.created_by} />
                    <Provenance
                      label="Last changed"
                      at={group.updated_at}
                      by={group.updated_by}
                    />
                  </div>
                </div>
              </div>

              {/* f4.2. Beside the Details tab, never inside it: ownership is a set
                  of assignments with their own lifecycle and their own audit
                  entries, not two more fields on the group. */}
              <OwnershipCard
                nodeType="BUSINESS_UNIT_GROUP"
                nodeId={group.id}
                nodeName={group.name}
                roleCodes={GROUP_ROLE_CODES}
                deliveryRoles={deliveryRoles}
                mayManage={rowCan(group, ASSIGN_OWNER)}
                emptyHint="Nobody is named on this group yet. Any Atlas account can be assigned here, including one nobody has signed in to yet."
              />
            </div>
            ),
          },
          {
            id: 'artifacts',
            label: 'Artifacts',
            icon: <FileText aria-hidden="true" />,
            count: group.artifact_count,
            panel: (
              <ArtifactsPanel
                ownerType="BUSINESS_UNIT_GROUP"
                ownerId={group.id}
                ownerName={group.name}
                mayPublish={rowCan(group, PUBLISH_ARTIFACT)}
              />
            ),
          },
        ]}
      />
    </>
  );
}
